import React, { FC, KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Form as FormikForm, Formik, FormikHelpers } from 'formik';
import { Divider, Grid, makeStyles, Paper, Typography } from '@material-ui/core';
import moment from 'moment';

import { ContentEmailSpecInput, useCreateContentEmailMutation, UserSegmentFragment } from 'api';
import { useProtectedClient, useNotify } from 'hooks';
import * as paths from 'paths';
import { formatStat } from 'helpers';

import { ContentEmailSchema } from 'features/admin/validations';
import {
  NO_SECONDS,
  MONTHLY_SECONDS,
  CONTENT_EMAIL_FALLBACK_EMAIL,
} from 'features/admin/constants';
import ContentEmailsForm from 'features/admin/components/ContentEmails/Partials/Form';

interface Props {
  segments: UserSegmentFragment[];
  userCount: number;
}

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(3),
  },
}));

const EmailCreate: FC<Props> = ({ segments, userCount }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const classes = useStyles();
  const client = useProtectedClient();
  const notify = useNotify();

  const [recipientCount, setRecipientCount] = useState(getInitialCount() || 0);

  const { mutateAsync: createEmail } = useCreateContentEmailMutation();

  function getInitialCount() {
    return userCount;
  }

  function handleCompleted() {
    notify.info({ message: t('contentEmails:saveSuccess') });
    history.push(paths.adminContentEmails);
  }

  function getEmailToUse() {
    const { email_sending_domain, email_from_address } = client;

    if (!email_sending_domain) {
      return CONTENT_EMAIL_FALLBACK_EMAIL;
    }

    const emailToUse = email_from_address?.split('@')?.[0] ?? '';
    return `${emailToUse}@${email_sending_domain}`;
  }

  function getInitialValues(): ContentEmailSpecInput {
    return {
      send_at: moment().add('1', 'h').unix(),
      delivery_interval: NO_SECONDS,
      grouping_interval: MONTHLY_SECONDS,
      all_users: true,
      title: '',
      segment_ids: [],
      content_specs: [],
      email_from: getEmailToUse(),
      email_reply_to: getEmailToUse(),
      banner: null,
      banner_url: null,
      subject: '',
      footer: '',
      intro: '',
      use_sto: true,
    };
  }

  function handleKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  async function handleSubmit(
    values: ContentEmailSpecInput,
    formikHelpers: FormikHelpers<ContentEmailSpecInput>
  ) {
    try {
      await createEmail({ data: values });
      handleCompleted();
    } catch (e) {
      formikHelpers.setSubmitting(false);
      notify.mutationError();
    }
  }

  return (
    <Paper>
      <div className={classes.container}>
        <Grid container spacing={4} alignItems="center" justify="space-between">
          <Grid item>
            <Typography variant="h4">{t('contentEmails:createTitle')}</Typography>
          </Grid>

          <Grid item>
            <Typography display="inline" variant="subtitle2">{`${t(
              'messages:recipients'
            )}: `}</Typography>
            <Typography display="inline" variant="subtitle2" color="primary">
              {formatStat(recipientCount)}
            </Typography>
          </Grid>
        </Grid>
      </div>
      <Divider />
      <Formik
        initialValues={getInitialValues()}
        validationSchema={ContentEmailSchema(client)}
        onSubmit={handleSubmit}
      >
        <FormikForm onKeyDown={handleKeyDown}>
          <div className={classes.container}>
            <ContentEmailsForm
              segments={segments}
              updateCount={setRecipientCount}
              totalUserCount={userCount}
            />
          </div>
        </FormikForm>
      </Formik>
    </Paper>
  );
};

export default EmailCreate;
